import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Bed,
  Ruler,
  MapPin,
  MoreVertical,
  Eye,
  Pencil,
  Archive,
  ArchiveRestore,
  Handshake,
  Megaphone,
  FileSignature,
  Gavel,
  CheckCircle2,
  Check,
  CheckCheck,
  type LucideIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { type PropertyRow, useDeleteProperty, useRestoreProperty } from '@/hooks/data/useProperties';
import { toast } from 'sonner';
import { ViewPropertyDialog } from './ViewPropertyDialog';
import { useConfirm } from '@/components/ui/confirm';
import { TrafficLight } from '@/components/ui/TrafficLight';
import { formatMoney, formatNumber } from '@/lib/formatLocale';
import { apiAssetUrl } from '@/lib/api';

// Objektfotos kommen als PFAD („/properties/…/primary-image"), nicht als
// fertige Adresse — die API weiß nicht, unter welchem Ursprung der Browser
// sie erreicht. Aufgelöst wird gegen `VITE_API_URL`, dieselbe Auflösung, die
// `<Avatar>` und `mediaFileUrl` schon machen; der Session-Cookie gilt für die
// ganze Domain, der auth-geschützte Stream ist also erreichbar.
//
// Bis 2026-09 stand in der Spalte `${origin}/files/<key>` bzw.
// `https://cdn.4m-immo.com/<key>` — eine Route, die es nie gab, und ein Host,
// der nicht auflöst. Jede der 48 Fotozeilen in D1 lieferte einen 404.
// Die Auflösung selbst liegt in `lib/api.ts` — sie wird an mehr als einer
// Stelle gebraucht, und genau daran ist sie beim ersten Mal gescheitert: die
// Fotokarte bekam den Rückfall, der Fotoreiter im Detaildialog nicht.
const imageSrc = apiAssetUrl;

interface Props {
  property: PropertyRow;
  isAdmin: boolean;
  onEdit?: (p: PropertyRow) => void;
  /** When provided, render a small "Compare" checkbox in the corner. */
  compareSelected?: boolean;
  onToggleCompare?: () => void;
  /** Foto des verbundenen Inserats, falls das Objekt kein eigenes hat. */
  listingImageUrl?: string | null;
}

// Batch AR (tester 2026-05-31) — stronger visual distinction between
// Akquise / Vermarktung / Verkauft so the maker / CC never confuses
// the lifecycle phase at a glance. Each phase gets:
//   • Left-edge border (3 px) — color-coded per phase
//   • Full-card class — subtle wash to differentiate from neighbours:
//     - Akquise (lead / aufnahme / pending): neutral, slightly muted
//     - Vermarktung (active): clear gold-tinted background → "this is
//       what you're actively selling"
//     - Anbot/Notar (kaufanbot_angenommen / notar_termin_geplant): warn
//       amber-tint → in-progress deal
//     - Verkauft (sold / notariell_verkauft): green-tinted + reduced
//       opacity → done, archive-feel, not actively worked
function stageBorderClass(stage: PropertyRow['marketing_stage']): string {
  switch (stage) {
    case 'sold':
    case 'notariell_verkauft':
      return 'border-s-[3px] border-s-[hsl(var(--positive))]';
    case 'kaufanbot_angenommen':
    case 'notar_termin_geplant':
      return 'border-s-[3px] border-s-[hsl(var(--warning))]';
    case 'pending':
    case 'aufnahme':
      return 'border-s-[3px] border-s-[hsl(var(--warning))]';
    case 'lead':
      return 'border-s-[3px] border-s-line-strong';
    case 'active':
      return 'border-s-[3px] border-s-gold';
    default:
      return '';
  }
}

// Subtle background wash per phase — operator scanning a grid of cards
// can tell at a glance which ones are "active" (= focus today), which
// are "in progress" (= waiting on notary), which are "done" (= archived
// but still visible for history).
function stageBackgroundClass(stage: PropertyRow['marketing_stage']): string {
  switch (stage) {
    case 'active':
      // Vermarktung — clear gold tint, the "working" surface.
      return 'bg-[hsl(var(--brand-gold)/0.04)]';
    case 'kaufanbot_angenommen':
    case 'notar_termin_geplant':
      // In-progress deal — gentle amber wash so it stands apart from
      // both active marketing and done.
      return 'bg-[hsl(var(--warning)/0.05)]';
    case 'sold':
    case 'notariell_verkauft':
      // Done — green wash + de-emphasised text via opacity-90.
      return 'bg-[hsl(var(--positive)/0.06)] opacity-90';
    case 'lead':
    case 'aufnahme':
    case 'pending':
      // Akquise — keep the default card surface, no wash. This makes
      // active properties visually pop next to akquise cards.
      return '';
    default:
      return '';
  }
}

const STAGE_LABELS: Record<PropertyRow['marketing_stage'], string> = {
  lead: 'Lead',
  aufnahme: 'Aufnahme',
  active: 'Aktiv',
  pending: 'Pending',
  sold: 'Verkauft',
  kaufanbot_angenommen: 'Kaufanbot angenommen',
  notar_termin_geplant: 'Notar-Termin',
  notariell_verkauft: 'Notariell verkauft',
};

const STAGE_PILL: Record<PropertyRow['marketing_stage'], string> = {
  lead: 'fm-pill-neutral',
  aufnahme: 'fm-pill-warn',
  active: 'fm-pill-gold',
  pending: 'fm-pill-warn',
  sold: 'fm-pill-pos',
  kaufanbot_angenommen: 'fm-pill-warn',
  notar_termin_geplant: 'fm-pill-info',
  notariell_verkauft: 'fm-pill-pos',
};

// v2 — small Lucide icon inside the stage pill gives operators a second
// perception channel (icon + colour) so they recognise the phase at a glance
// without reading the label. Handshake = trying to acquire the listing,
// Megaphone = actively marketing, FileSignature = offer accepted, Gavel =
// notary scheduled, CheckCircle2 = sold.
const STAGE_ICON: Record<PropertyRow['marketing_stage'], LucideIcon> = {
  lead: Handshake,
  aufnahme: Handshake,
  pending: Handshake,
  active: Megaphone,
  kaufanbot_angenommen: FileSignature,
  notar_termin_geplant: Gavel,
  notariell_verkauft: CheckCircle2,
  sold: CheckCircle2,
};

export function PropertyCard({ property: p, isAdmin, onEdit, compareSelected, onToggleCompare, listingImageUrl }: Props) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [viewing, setViewing] = useState(false);
  // Ein Foto kann fehlen, ohne dass die Zeile es weiß — gelöschte Bytes, ein
  // Schlüssel aus der Altmigration, ein Objekt ohne Leserecht. Dann gehört
  // dorthin das Platzhalter-Icon und kein zerbrochenes Bild. Gemerkt wird die
  // Adresse, nicht ein Ja/Nein: lädt der Makler danach ein neues Foto hoch,
  // soll die Karte es zeigen und nicht bis zum Neuaufbau beim Platzhalter
  // bleiben.
  const [brokenSrcs, setBrokenSrcs] = useState<string[]>([]);
  const photo = [p.primary_image_url, listingImageUrl].find((u): u is string => !!u && !brokenSrcs.includes(u));
  const archive = useDeleteProperty();
  const restore = useRestoreProperty();
  const isArchived = p.archived_at != null;

  const fullAddress = [p.address, p.postal_code, p.city].filter(Boolean).join(' · ');

  const onArchive = async () => {
    const ok = await confirm({
      title: t('properties.archive.title', 'Immobilie archivieren?'),
      body: p.address ?? p.city,
      confirmLabel: t('common.archive', 'Archivieren'),
      destructive: true,
    });
    if (!ok) return;
    try {
      await archive.mutateAsync(p.id);
      toast.success(t('properties.archive.done', 'Archiviert.'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const onRestore = async () => {
    try {
      await restore.mutateAsync(p.id);
      toast.success(t('properties.restore.done', 'Wiederhergestellt.'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  return (
    <>
      <article
        className={`fm-card interactive overflow-hidden ${stageBorderClass(
          p.marketing_stage,
        )} ${stageBackgroundClass(p.marketing_stage)}`}
        onClick={() => setViewing(true)}
      >
        {/* Image */}
        <div className="fm-photo aspect-[16/10] relative">
          {photo ? (
            <img
              src={imageSrc(photo)}
              alt={p.address ?? p.city}
              loading="lazy"
              onError={() => setBrokenSrcs((prev) => [...prev, photo])}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-paper/60">
              <Building2 className="h-10 w-10" />
            </div>
          )}
          <div className="absolute end-2 top-2 flex items-center gap-1.5">
            <KaufanbotBadge
              status={p.kaufanbot_status ?? 'none'}
              buyers={p.kaufanbot_buyers ?? []}
            />
            {(() => {
              const StageIcon = STAGE_ICON[p.marketing_stage];
              return (
                <span
                  className={`fm-pill inline-flex items-center gap-1 ${STAGE_PILL[p.marketing_stage]}`}
                >
                  <StageIcon className="h-3 w-3" />
                  {t(`enums.marketing_stage.${p.marketing_stage}`, STAGE_LABELS[p.marketing_stage])}
                </span>
              );
            })()}
          </div>
          {onToggleCompare && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleCompare();
              }}
              className={`absolute start-2 top-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider backdrop-blur-sm transition-colors ${
                compareSelected
                  ? 'border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold))]/30 text-white'
                  : 'border-white/30 bg-black/30 text-white/85 hover:border-white/60'
              }`}
              title={t('properties.compare.toggle', 'Zum Vergleich hinzufügen')}
            >
              <span
                className={`inline-block h-2.5 w-2.5 rounded-sm border ${
                  compareSelected ? 'border-white bg-white' : 'border-white/60'
                }`}
                aria-hidden
              />
              {t('properties.compare.short', 'Vergleichen')}
            </button>
          )}
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-base text-ink truncate flex items-center gap-2">
                <TrafficLight state={p.traffic_light} />
                <span className="truncate">{p.address ?? p.city ?? '—'}</span>
              </h3>
              <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-ink-3">
                <MapPin className="h-3 w-3" />
                {[p.postal_code, p.district, p.city].filter(Boolean).join(' · ')}
              </p>
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-3 hover:bg-paper-2 hover:text-ink"
                    aria-label="Aktionen"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setViewing(true)}>
                    <Eye className="h-3.5 w-3.5" />
                    {t('properties.actions.view', 'Ansehen')}
                  </DropdownMenuItem>
                  {onEdit && (
                    <DropdownMenuItem onClick={() => onEdit(p)}>
                      <Pencil className="h-3.5 w-3.5" />
                      {t('properties.actions.edit', 'Bearbeiten')}
                    </DropdownMenuItem>
                  )}
                  {/* Archive / Restore — admin-only per business policy:
                      makler / call_center can edit but not delete records. */}
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      {isArchived ? (
                        <DropdownMenuItem onClick={onRestore} disabled={restore.isPending}>
                          <ArchiveRestore className="h-3.5 w-3.5" />
                          {t('properties.actions.restore', 'Wiederherstellen')}
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          variant="danger"
                          onClick={onArchive}
                          disabled={archive.isPending}
                        >
                          <Archive className="h-3.5 w-3.5" />
                          {t('properties.actions.archive', 'Archivieren')}
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-2">
            {p.rooms_count && (
              <span className="inline-flex items-center gap-1">
                <Bed className="h-3 w-3 text-ink-3" />
                {p.rooms_count} Zi
              </span>
            )}
            {p.property_size && (
              <span className="inline-flex items-center gap-1">
                <Ruler className="h-3 w-3 text-ink-3" />
                {p.property_size} m²
              </span>
            )}
            {p.building_type && (
              <span className="fm-pill fm-pill-outline !text-[10px]">
                {t(`enums.building_type.${p.building_type}`, p.building_type)}
              </span>
            )}
          </div>

          {p.purchase_price && (
            <p className="font-display text-xl text-ink">
              € {formatNumber(Number(p.purchase_price), { maximumFractionDigits: 0 })}
            </p>
          )}
        </div>
      </article>

      {viewing && (
        <ViewPropertyDialog
          property={p}
          isAdmin={isAdmin}
          open={true}
          onOpenChange={(o) => !o && setViewing(false)}
        />
      )}
    </>
  );
}

// Batch AM — Kaufanbot indicator. Renders a small dark chip with one or
// two check marks. Native `title=""` tooltip lists buyer names + offered
// prices, so it works on touch (long-press) and mouse (hover) without
// pulling in a heavier popover lib for a 2-state badge.
function KaufanbotBadge({
  status,
  buyers,
}: {
  status: 'accepted' | 'active' | 'none';
  buyers: NonNullable<PropertyRow['kaufanbot_buyers']>;
}) {
  if (status === 'none') return null;
  const isAccepted = status === 'accepted';
  const label = isAccepted ? 'Kaufanbot angenommen' : 'Kaufanbot aktiv';
  const tooltip =
    buyers.length > 0
      ? buyers
          .map((b) => {
            const price =
              b.price != null
                ? ` · ${formatMoney(Number(b.price), {
                    maximumFractionDigits: 0,
                  })}`
                : '';
            return `${b.name ?? '—'}${price}`;
          })
          .join('\n')
      : label;
  return (
    <span
      title={`${label}\n${tooltip}`}
      className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider backdrop-blur-sm ${
        isAccepted
          ? 'border-[hsl(var(--positive))] bg-[hsl(var(--positive)/0.85)] text-white'
          : 'border-[hsl(var(--brand-gold-deep))] bg-[hsl(var(--brand-gold)/0.85)] text-ink'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {isAccepted ? (
        <CheckCheck className="h-3 w-3" />
      ) : (
        <Check className="h-3 w-3" />
      )}
    </span>
  );
}
